import { type SetStateAction } from 'react';
import type React from 'react';
import { type AppDispatch } from '@/store';
import { type TrackerPath, type Vehicle } from '@/types/swagger';
import { type GridValidRowModel } from '@mui/x-data-grid';
import { type UnknownAction } from '@reduxjs/toolkit';
import { loadChargePointsByGridConnectionId, setSelectedChargePoint } from 'features/ChargePoints';
import {
  loadConnectors,
  loadConnectorsByGridConnectionId,
  loadConnectorsOnline,
  loadOnlineConnectorsByGridConnectionId,
  setSelectedConnector,
} from 'features/Connectors';
import { addDownloadFile } from 'features/Downloads';
import {
  loadChargePointTransactions,
  loadSampledValuesByTransactionId,
  setSelectedTransaction,
} from 'features/Transactions';

import { clickButtonById } from './button-click';
import {
  dynamicDownloads,
  onboardingChargePoint,
  onboardingConnectors,
  onboardingConnectorsFaulted,
  onboardingConnectorsUnavailable,
  onboardingTrackerLogs,
  onboardingTrackerPaths,
  onboardingVehicle,
  sampledValues,
  trackerPathWithLogs,
  transactionOnboardingData,
} from './data';
import { onboardingHistoricalUptime } from './data/historical-uptime';
import { scrollToTooltipFix, scrollToTooltipFixRevert } from './tooltip-scrolling';

export const chargeCenterIndexActions = (stepNumber: number) => {
  if (stepNumber === 0 || stepNumber === 5) {
    scrollToTooltipFixRevert();
    return;
  }
  if (stepNumber === 1 || stepNumber === 4) {
    scrollToTooltipFix();
  }
};

export const chargeCenterActions = (
  stepNumber: number,
  actionProps: {
    dispatch: AppDispatch;
    gridConnectionId: number;
    setSelectedTab: (tab: number) => void;
    openModal: () => void;
    closeModal: () => void;
  }
) => {
  const { dispatch, gridConnectionId, setSelectedTab, openModal, closeModal } = actionProps;
  if (stepNumber === 0) {
    scrollToTooltipFix();
    dispatch(
      loadChargePointsByGridConnectionId(gridConnectionId, {
        '-1': {
          ...onboardingChargePoint(gridConnectionId)['-1'],
          status: 'Offline',
        },
      })
    );
    return;
  }
  if (stepNumber === 1) {
    dispatch(
      loadChargePointsByGridConnectionId(gridConnectionId, onboardingChargePoint(gridConnectionId))
    );
    dispatch(loadConnectors(onboardingConnectors()));
    dispatch(loadConnectorsOnline(onboardingConnectors()));
    dispatch(loadOnlineConnectorsByGridConnectionId(gridConnectionId, onboardingConnectors()));
    return;
  }
  if (stepNumber === 2) {
    dispatch(loadConnectors(onboardingConnectorsFaulted()));
    dispatch(loadConnectorsOnline(onboardingConnectorsFaulted()));
    dispatch(
      loadOnlineConnectorsByGridConnectionId(gridConnectionId, onboardingConnectorsFaulted())
    );
    return;
  }
  if (stepNumber === 3) {
    dispatch(loadConnectors(onboardingConnectorsUnavailable()));
    dispatch(loadConnectorsOnline(onboardingConnectorsUnavailable()));
    dispatch(
      loadOnlineConnectorsByGridConnectionId(gridConnectionId, onboardingConnectorsUnavailable())
    );
    dispatch(setSelectedChargePoint(null));
    return;
  }
  if (stepNumber === 4) {
    scrollToTooltipFix();
    dispatch(setSelectedChargePoint(onboardingChargePoint(gridConnectionId)['-1']));
    dispatch(loadChargePointTransactions(-1, { '-1': transactionOnboardingData }));
    dispatch(setSelectedTransaction(transactionOnboardingData));
    dispatch(loadConnectors(onboardingConnectors()));
    dispatch(loadConnectorsOnline(onboardingConnectors()));
    dispatch(loadOnlineConnectorsByGridConnectionId(gridConnectionId, onboardingConnectors()));
    return;
  }
  if (stepNumber === 5) {
    scrollToTooltipFixRevert();
    dispatch(loadSampledValuesByTransactionId(-1, Object.values(sampledValues), true));
    return;
  }
  if (stepNumber === 6) {
    scrollToTooltipFixRevert();
    closeModal();
    dispatch(setSelectedChargePoint(onboardingChargePoint(gridConnectionId)['-1']));
    dispatch(loadChargePointTransactions(-1, { '-1': transactionOnboardingData }));
    dispatch(setSelectedTransaction(transactionOnboardingData));
    setSelectedTab(0);
    dispatch(loadSampledValuesByTransactionId(-1, sampledValues, false));
    return;
  }
  if (stepNumber === 7) {
    dispatch(setSelectedChargePoint(null));
    closeModal();
    return;
  }
  if (
    stepNumber === 8 ||
    stepNumber === 9 ||
    stepNumber === 10 ||
    stepNumber === 11 ||
    stepNumber === 12 ||
    stepNumber === 13 ||
    stepNumber === 14
  ) {
    scrollToTooltipFixRevert();
    openModal();
    return;
  }
  if (stepNumber === 15) {
    scrollToTooltipFix();
    openModal();
    return;
  }
  if (stepNumber === 16) {
    scrollToTooltipFix();
    openModal();
    return;
  }
  if (stepNumber === 17) {
    scrollToTooltipFix();
    closeModal();
    dispatch(loadChargePointTransactions(-1, { '-1': transactionOnboardingData }));
    dispatch(setSelectedTransaction(transactionOnboardingData));
    setSelectedTab(0);
    dispatch(loadSampledValuesByTransactionId(-1, sampledValues, false));
    return;
  }
};

export const chargeCenterActionsConnectors = (
  stepNumber: number,
  actionProps: {
    dispatch: AppDispatch;
    gridConnectionId: number;
    setSelectedTab: (tab: number) => void;
    openModal: () => void;
    closeModal: () => void;
  }
) => {
  const { dispatch, gridConnectionId: _, setSelectedTab } = actionProps;
  if (stepNumber === 0) {
    dispatch(setSelectedChargePoint(null));
    setSelectedTab(1);
    return;
  }
  if (stepNumber === 1) {
    dispatch(setSelectedConnector(null));
    return;
  }
  if (stepNumber === 2) {
    scrollToTooltipFix();
    dispatch(setSelectedConnector(onboardingConnectors()['-2']));
    return;
  }
  if (stepNumber === 3 || stepNumber === 7) {
    scrollToTooltipFixRevert();
  }
};

export const remoteManagementIndexActions = (stepNumber: number) => {
  if (stepNumber === 0 || stepNumber === 3) {
    scrollToTooltipFixRevert();
    return;
  }
  if (stepNumber === 1 || stepNumber === 2) {
    scrollToTooltipFix();
  }
};

export const remoteManagementActions = (
  stepNumber: number,
  actionProps: {
    dispatch: AppDispatch;
    selectTab: (tab: number) => UnknownAction;
    handleEdit: (edit: boolean) => void;
    gridConnectionId: number;
  }
) => {
  const { dispatch, selectTab, handleEdit, gridConnectionId } = actionProps;
  if (stepNumber === 0) {
    scrollToTooltipFix();
    dispatch(
      loadChargePointsByGridConnectionId(gridConnectionId, onboardingChargePoint(gridConnectionId))
    );
    dispatch(loadConnectors(onboardingConnectors()));
    dispatch(loadConnectorsByGridConnectionId(gridConnectionId, onboardingConnectors()));
    return;
  }
  if (stepNumber === 1) {
    dispatch(selectTab(0));
    return;
  }
  if (stepNumber === 2) {
    dispatch(selectTab(1));
    return;
  }
  if (stepNumber === 5) {
    handleEdit(false);
    return;
  }
  if (stepNumber === 6) {
    scrollToTooltipFix();
    handleEdit(true);
    return;
  }
  if (stepNumber === 7) {
    scrollToTooltipFixRevert();
    handleEdit(false);
  }
};

export const fleetActions = (
  stepNumber: number,
  actionProps: {
    setSelectedTab: (tab: number) => void;
    setVehicles: React.Dispatch<SetStateAction<Vehicle[]>>;
    selectVehicle: (vehicle: string | null) => UnknownAction;
    setDrawerOpen: (open: number) => void;
    setTrackerPaths: React.Dispatch<SetStateAction<TrackerPath[]>>;
    setSelectedTrackerPath: (trackerPath: TrackerPath | null) => void;
  }
) => {
  if (stepNumber === 0) {
    scrollToTooltipFixRevert();
    !!actionProps?.setVehicles &&
      actionProps.setVehicles?.((vehicles) => {
        const newVehicles = [...vehicles];
        newVehicles.push(onboardingVehicle);
        return newVehicles;
      });
    return;
  }
  if (stepNumber === 2) {
    scrollToTooltipFix();
    !!actionProps?.selectVehicle && actionProps.selectVehicle(null);
    !!actionProps?.setDrawerOpen && actionProps.setDrawerOpen(0);
    return;
  }
  if (stepNumber === 3) {
    scrollToTooltipFix();
    !!actionProps?.setDrawerOpen && actionProps.setDrawerOpen(1);
    !!actionProps?.selectVehicle && actionProps.selectVehicle('ENP2019');
    return;
  }
  if (stepNumber === 4) {
    scrollToTooltipFixRevert();
    !!actionProps?.setDrawerOpen && actionProps.setDrawerOpen(1);
    return;
  }
  if (stepNumber === 5) {
    !!actionProps?.setDrawerOpen && actionProps.setDrawerOpen(2);
    !!actionProps?.setTrackerPaths && actionProps.setTrackerPaths([]);
    return;
  }
  if (stepNumber === 6) {
    !!actionProps?.setSelectedTrackerPath && actionProps.setSelectedTrackerPath(null);
    !!actionProps?.setTrackerPaths &&
      actionProps.setTrackerPaths(
        trackerPathWithLogs(onboardingTrackerPaths, [onboardingTrackerLogs, [], [], [], []])
      );
    return;
  }
  if (stepNumber === 7) {
    !!actionProps?.setSelectedTrackerPath && actionProps.setSelectedTrackerPath(null);
    !!actionProps?.setTrackerPaths &&
      actionProps.setTrackerPaths(
        trackerPathWithLogs(onboardingTrackerPaths, [onboardingTrackerLogs, [], [], [], []])
      );
    return;
  }
  if (stepNumber === 8) {
    scrollToTooltipFixRevert();
    !!actionProps?.setSelectedTrackerPath && actionProps.setSelectedTrackerPath(null);
    !!actionProps?.setTrackerPaths &&
      actionProps.setTrackerPaths(
        trackerPathWithLogs(onboardingTrackerPaths, [onboardingTrackerLogs, [], [], [], []])
      );
    !!actionProps?.setSelectedTrackerPath &&
      actionProps.setSelectedTrackerPath(onboardingTrackerPaths[0]);
    return;
  }
  if (stepNumber === 9) {
    scrollToTooltipFix();
    return;
  }
  if (stepNumber === 10) {
    !!actionProps?.setSelectedTab && actionProps.setSelectedTab(0);
    !!actionProps?.selectVehicle && actionProps.selectVehicle('ENP2019');
    !!actionProps?.setDrawerOpen && actionProps.setDrawerOpen(2);
    !!actionProps?.setTrackerPaths &&
      actionProps.setTrackerPaths(
        trackerPathWithLogs(onboardingTrackerPaths, [onboardingTrackerLogs, [], [], [], []])
      );
    return;
  }
};

export const fleetActionsSummary = (
  stepNumber: number,
  actionProps: {
    setSelectedTab: (tab: number) => void;
    setVehicles: React.Dispatch<SetStateAction<Vehicle[]>>;
    selectVehicle: (vehicle: string | null) => UnknownAction;
    setDrawerOpen: (open: number) => void;
    setTrackerPaths: React.Dispatch<SetStateAction<TrackerPath[]>>;
    setSelectedTrackerPath: (trackerPath: TrackerPath | null) => void;
  }
) => {
  if (stepNumber === 0) {
    !!actionProps?.setSelectedTab && actionProps.setSelectedTab(1);
    !!actionProps?.setDrawerOpen && actionProps.setDrawerOpen(0);
    !!actionProps?.selectVehicle && actionProps.selectVehicle(null);
    return;
  }
  if (stepNumber === 1) {
    scrollToTooltipFix();
    return;
  }
  if (stepNumber === 2) {
    scrollToTooltipFixRevert();
  }
};

export const historicalUptimeActions = (
  stepNumber: number,
  actionProps: {
    createdRows: GridValidRowModel[];
    setRows: React.Dispatch<SetStateAction<GridValidRowModel[]>>;
  }
) => {
  const table = document.querySelector('.table-with-expandable-rows-wrapper');
  const helperLayer = document.querySelector('.introjs-helperLayer');
  const firstRow = document.querySelector('#expandable-row-0');
  const firstRowExpandButtonId = '#expand-row-0-button';
  const isFirstRowExpanded = !!firstRow?.classList.contains('expanded');

  const setHelperLayerWidth = (width: number | undefined) => {
    if (!helperLayer || width) return;
    const newStyle = helperLayer
      .getAttribute('style')
      ?.replace(' ', '')
      ?.split(';')
      .map((s) => (s.includes('width') ? `width:${width}px!important` : s))
      .join(';');
    helperLayer.setAttribute('style', newStyle || '');
  };

  // Use mocked data only if user has an empty table
  const conditionToUseMockedData =
    !actionProps?.createdRows || (actionProps?.createdRows && actionProps.createdRows.length === 0);
  conditionToUseMockedData &&
    !!actionProps?.setRows &&
    actionProps.setRows(onboardingHistoricalUptime);

  if (stepNumber === 0) {
    scrollToTooltipFixRevert();
    return;
  }

  // Filters
  if (stepNumber === 1 || stepNumber === 2) {
    scrollToTooltipFix();
    if (isFirstRowExpanded) clickButtonById(firstRowExpandButtonId);
    return;
  }

  // Chargepoint
  if (stepNumber === 3) {
    if (!isFirstRowExpanded) clickButtonById(firstRowExpandButtonId);
    setHelperLayerWidth(table?.clientWidth);
    return;
  }

  // Connector
  if (stepNumber === 4) {
    if (!isFirstRowExpanded) clickButtonById(firstRowExpandButtonId);
    setHelperLayerWidth(table?.clientWidth);
    return;
  }

  if (stepNumber === 5) {
    scrollToTooltipFixRevert();
    if (!isFirstRowExpanded) clickButtonById(firstRowExpandButtonId); // leave row expanded in case of going back to step 4
    setHelperLayerWidth(0);
  }
};

export const adminHistories = (
  stepNumber: number,
  actionProps?: {
    dispatch: AppDispatch;
  }
) => {
  if (stepNumber === 0 || stepNumber === 3) {
    scrollToTooltipFixRevert();
    return;
  }
  if (stepNumber === 1 || stepNumber === 2) {
    scrollToTooltipFix();
    return;
  }
  if (stepNumber === 4) {
    scrollToTooltipFix();
    dynamicDownloads.forEach((file) => {
      actionProps?.dispatch?.(addDownloadFile(file));
    });
  }
};

export const adminUserActions = (
  stepNumber: number,
  actionProps: {
    setDialogOpen: React.Dispatch<SetStateAction<boolean>>;
  }
) => {
  if (stepNumber === 0) {
    scrollToTooltipFixRevert();
    return;
  }
  if (stepNumber === 1) {
    scrollToTooltipFix();
    !!actionProps?.setDialogOpen && actionProps.setDialogOpen(false);
    return;
  }
  if (stepNumber === 2) {
    !!actionProps?.setDialogOpen && actionProps.setDialogOpen(true);
    return;
  }
  if (stepNumber === 4) {
    scrollToTooltipFix();
    return;
  }
  if (stepNumber === 5) {
    scrollToTooltipFixRevert();
  }
};

export const adminFleetActions = (
  stepNumber: number,
  actionProps: {
    closeModal: () => void;
    openModal: () => void;
  }
) => {
  if (stepNumber === 0) {
    scrollToTooltipFixRevert();
    return;
  }
  if (stepNumber === 1) {
    scrollToTooltipFix();
    !!actionProps?.closeModal && actionProps.closeModal();
    return;
  }
  if (stepNumber === 2) {
    !!actionProps?.openModal && actionProps.openModal();
    return;
  }
  if (stepNumber === 3) {
    scrollToTooltipFix();
    !!actionProps?.openModal && actionProps.openModal();
    return;
  }
  if (stepNumber === 4) {
    scrollToTooltipFixRevert();
    !!actionProps?.closeModal && actionProps.closeModal();
  }
};
